// CreateReferralModal.js
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Button, CardContent, CardActions, Divider, Grid, IconButton, Modal, TextField, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MainCard from 'ui-component/cards/MainCard';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axiosServices from 'utils/axios';
import { toast } from 'react-toastify';

const statusOptions = ['SENT', 'NOT_SENT', 'DETAILS_MISSING'];
const agentTenantOptions = ['AGENT', 'TENANT'];

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    contact: Yup.string().required('Contact is required'),
    complexes: Yup.array().min(1, 'Select at least one complex'),
    managementCompany: Yup.string().required('Management Company is required'),
    regional: Yup.string().required('Regional is required'),
    agentTenant: Yup.string().oneOf(agentTenantOptions).required('Select Agent/Tenant'),
    source: Yup.string().required('Source is required'),
    status: Yup.string().oneOf(statusOptions).required('Status is required'),
    receivedDate: Yup.date().required('Received Date is required'),
    sentDate: Yup.date().required('Sent Date is required')
});

export default function CreateReferralModal({ open, setOpen, editItem, clearEdit, refresh }) {
    const [complexesList, setComplexesList] = useState([]);

    useEffect(() => {
        axiosServices
            .get('/complex?page=1&limit=1000', {
                headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
            })
            .then((res) => {
                console.log(res);
                setComplexesList(res.data.complexes || []);
            })
            .catch(() => toast.error('Error fetching complexes'));
    }, []);

    const initialValues = {
        name: editItem?.name || '',
        email: editItem?.email || '',
        contact: editItem?.contact || '',
        complexes: editItem?.complexes.map((c) => c.id) || [],
        managementCompany: editItem?.managementCompany || '',
        regional: editItem?.regional || '',
        agentTenant: editItem?.agentTenant || '',
        source: editItem?.source || '',
        status: editItem?.status || '',
        receivedDate: editItem?.receivedDate?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        sentDate: editItem?.sentDate?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        interested: editItem?.interested || ''
    };

    const handleClose = () => {
        clearEdit();
        setOpen(false);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        const payload = { ...values };
        try {
            if (editItem) {
                await axiosServices.patch(`/referrals/${editItem.id}`, payload, {
                    headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
                });
                toast.success('Referral updated');
            } else {
                await axiosServices.post('/referrals', payload, {
                    headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
                });
                toast.success('Referral created');
            }
            refresh();
            handleClose();
        } catch {
            toast.error('Error saving referral');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <MainCard
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 350, sm: 500 }
                }}
                title={editItem ? 'Edit Referral' : 'Create Referral'}
                secondary={
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                }
                content={false}
            >
                <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    {/* Complexes Multi-Select */}
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            multiple
                                            options={complexesList}
                                            getOptionLabel={(opt) => opt.name || opt.id}
                                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                            value={complexesList.filter((c) => values.complexes.includes(c.id))}
                                            onChange={(_, v) =>
                                                setFieldValue(
                                                    'complexes',
                                                    v.map((c) => c.id)
                                                )
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Complexes"
                                                    error={touched.complexes && Boolean(errors.complexes)}
                                                    helperText={touched.complexes && errors.complexes}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {/* Management Company, Regional, Source */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Management Company"
                                            name="managementCompany"
                                            value={values.managementCompany}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.managementCompany && Boolean(errors.managementCompany)}
                                            helperText={touched.managementCompany && errors.managementCompany}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Regional"
                                            name="regional"
                                            value={values.regional}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.regional && Boolean(errors.regional)}
                                            helperText={touched.regional && errors.regional}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Contact"
                                            name="contact"
                                            value={values.contact}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.contact && Boolean(errors.contact)}
                                            helperText={touched.contact && errors.contact}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Source"
                                            name="source"
                                            value={values.source}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.source && Boolean(errors.source)}
                                            helperText={touched.source && errors.source}
                                        />
                                    </Grid>
                                    {/* Agent/Tenant */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Agent/Tenant"
                                            name="agentTenant"
                                            value={values.agentTenant}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.agentTenant && Boolean(errors.agentTenant)}
                                            helperText={touched.agentTenant && errors.agentTenant}
                                        >
                                            {agentTenantOptions.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    {/* Status */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Status"
                                            name="status"
                                            value={values.status}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.status && Boolean(errors.status)}
                                            helperText={touched.status && errors.status}
                                        >
                                            {statusOptions.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    {/* Dates */}
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Received Date"
                                            name="receivedDate"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={values.receivedDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.receivedDate && Boolean(errors.receivedDate)}
                                            helperText={touched.receivedDate && errors.receivedDate}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Sent Date"
                                            name="sentDate"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={values.sentDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.sentDate && Boolean(errors.sentDate)}
                                            helperText={touched.sentDate && errors.sentDate}
                                        />
                                    </Grid>
                                    {/* Interested */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Interested Notes"
                                            name="interested"
                                            value={values.interested}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <Divider />
                            <CardActions sx={{ justifyContent: 'flex-end' }}>
                                <Button variant="outlined" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                                    {editItem ? 'Update' : 'Create'}
                                </LoadingButton>
                            </CardActions>
                        </form>
                    )}
                </Formik>
            </MainCard>
        </Modal>
    );
}

CreateReferralModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    editItem: PropTypes.object,
    clearEdit: PropTypes.func.isRequired,
    refresh: PropTypes.func.isRequired
};
